/**
 * RhythmEditor — the schedule, edited as the sentence it prints (YAG-647 §2).
 *
 * A manager says "on weekdays, every 2 hours from 8 to 6", so that is the
 * shape of the control: a mode (fixed times, or an interval inside a window),
 * the days as chips with the two presets anyone actually uses, and the
 * timezone the clock is read in. The sentence under the controls is the same
 * one the folded row prints, so what a person is about to save is written out
 * before they save it.
 *
 * It only ever produces the two live shapes, `{ times }` or `{ every }`, plus
 * `days` and `tz`. The compatibility shapes are read (an existing row still
 * says something true) and never written back: a legacy row opens on the
 * craft's own default and says so.
 *
 * Refusals are the server's. The editor blocks the one it can see coming (two
 * times inside 30 minutes) so a person is not told about it by a round trip,
 * and prints whatever code the server returns as a plain sentence. It says so
 * only once a person has edited something: a refusal on a schedule nobody has
 * touched yet is the page telling somebody off for arriving.
 *
 * Two modes, one editor:
 *
 *   - **Its own writer** (`onSave`): the Rhythms surface, where the editor's
 *     own button is the write and "Saved." is true.
 *   - **Controlled** (`onChange`): an open line on the Team page, where the
 *     editor is one blank in a longer form and the card's Save is the only
 *     save. Every edit writes straight through to the caller's draft, and the
 *     editor grows no button and keeps no saved state of its own.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type { Schedule, ScheduleDay } from "@yagni/shared";
import {
  RHYTHM_EDITOR_DAYS_LABEL,
  RHYTHM_EDITOR_EVERY_LABEL,
  RHYTHM_EDITOR_FROM_LABEL,
  RHYTHM_EDITOR_LEGACY_NOTE,
  RHYTHM_EDITOR_MODE_EVERY,
  RHYTHM_EDITOR_MODE_TIMES,
  RHYTHM_EDITOR_PRESET_EVERY_DAY,
  RHYTHM_EDITOR_PRESET_WEEKDAYS,
  RHYTHM_EDITOR_SAVE,
  RHYTHM_EDITOR_SAVED,
  RHYTHM_EDITOR_TIME_ADD,
  RHYTHM_EDITOR_TIME_REMOVE,
  RHYTHM_EDITOR_TIMES_LABEL,
  RHYTHM_EDITOR_TITLE,
  RHYTHM_EDITOR_TO_LABEL,
  RHYTHM_EDITOR_TZ_LABEL,
  RHYTHM_ERROR_COPY,
  RHYTHM_INTERVAL_LABEL,
  rhythmErrorLine,
} from "./copy.js";
import {
  DAY_CHIP_LABEL,
  MIN_TIME_SPACING_MINUTES,
  SCHEDULE_INTERVAL_MINUTES,
  WEEK_ORDER,
  WEEKDAYS,
  isEditableSchedule,
  parseHhmm,
  scheduleDays,
  scheduleSentence,
} from "./scheduleSentence.js";

/** At most three fixed times: past that a person wants an interval. */
const MAX_TIMES = 3;

/** Minutes past midnight as the "HH:MM" the time inputs read. */
function hhmm(minutes: number): string {
  const clamped = Math.max(0, Math.min(23 * 60 + 59, minutes));
  return `${String(Math.floor(clamped / 60)).padStart(2, "0")}:${String(clamped % 60).padStart(2, "0")}`;
}

/**
 * The time "add a time" adds.
 *
 * A fixed second default sat on top of the first one the moment a person
 * pressed the button twice, and the editor then refused its own suggestion for
 * being too close to itself. So the next time is the first free hour after the
 * latest one, and the refusal a person sees is one they made.
 */
export function nextFreeTime(times: readonly string[]): string {
  const taken = times
    .map((time) => parseHhmm(time))
    .filter((value): value is number => value !== null);
  const clear = (candidate: number) =>
    taken.every((value) => Math.abs(value - candidate) >= MIN_TIME_SPACING_MINUTES);
  const latest = taken.length > 0 ? Math.max(...taken) : 8 * 60;
  for (let at = latest + 60; at < 24 * 60; at += 60) {
    if (clear(at)) return hhmm(at);
  }
  for (let at = 0; at < 24 * 60; at += 60) {
    if (clear(at)) return hhmm(at);
  }
  return hhmm(latest + MIN_TIME_SPACING_MINUTES);
}

/** The timezones offered beside the row's own and the reader's own. */
const COMMON_TIMEZONES = [
  "UTC",
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "Europe/London",
  "Europe/Berlin",
  "Europe/Madrid",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
];

/** The reader's own clock, when the browser will say. */
export function browserTimezone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
  } catch {
    return null;
  }
}

function timezoneOptions(current: string | null): string[] {
  const out: string[] = [];
  for (const tz of [current, browserTimezone(), ...COMMON_TIMEZONES]) {
    if (tz && !out.includes(tz)) out.push(tz);
  }
  return out;
}

type Mode = "times" | "every";

export interface RhythmEditorProps {
  /** The schedule as it stands on the row, or null when nothing is set. */
  schedule: Schedule | null;
  /** The timezone the row is read in today. */
  tz: string | null;
  /** The craft's own default, used when the row carries no editable shape. */
  fallback: Schedule;
  disabled: boolean;
  /**
   * Writes the schedule; resolves the server's refusal code, or null. Absent
   * in controlled mode, where the editor grows no button of its own.
   */
  onSave?: (schedule: Schedule) => Promise<string | null>;
  /**
   * Controlled mode: every edit writes the whole schedule through to the
   * caller, which holds the draft and owns the save.
   */
  onChange?: (schedule: Schedule) => void;
  /** What the button says, when there is one. */
  saveLabel?: string;
  savedLabel?: string;
}

export function RhythmEditor({
  schedule,
  tz,
  fallback,
  disabled,
  onSave,
  onChange,
  saveLabel,
  savedLabel,
}: RhythmEditorProps) {
  // A row the sentence editor cannot round-trip opens on the craft default,
  // and the note above the controls says exactly that.
  const legacy = schedule !== null && !isEditableSchedule(schedule);
  const source = isEditableSchedule(schedule) ? (schedule as Schedule) : fallback;

  const [mode, setMode] = useState<Mode>(source.every ? "every" : "times");
  const [times, setTimes] = useState<string[]>(
    source.times && source.times.length > 0 ? [...source.times] : ["09:00"],
  );
  const [everyMinutes, setEveryMinutes] = useState<number>(source.every?.minutes ?? 120);
  const [from, setFrom] = useState<string>(source.every ? (source.every.between?.[0] ?? "00:00") : "08:00");
  const [to, setTo] = useState<string>(source.every ? (source.every.between?.[1] ?? "23:59") : "18:00");
  const [days, setDays] = useState<ScheduleDay[]>(() => {
    const chosen = scheduleDays(source);
    return chosen.length > 0 ? chosen : [...WEEK_ORDER];
  });
  const [zone, setZone] = useState<string>(tz ?? browserTimezone() ?? "UTC");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  // Nothing is wrong with a schedule a person has not touched yet, whatever
  // the controls say about the one they inherited.
  const [touched, setTouched] = useState(false);

  const draft = useMemo<Schedule>(() => {
    const base: Schedule = { days: [...days], tz: zone };
    if (mode === "every") {
      return { ...base, every: { minutes: everyMinutes, between: [from, to] } };
    }
    return { ...base, times: [...times] };
  }, [mode, times, everyMinutes, from, to, days, zone]);

  // The one refusal the editor can see coming, so nobody learns it from a
  // round trip: two fixed times inside the minimum spacing.
  const localProblem = useMemo<string | null>(() => {
    if (mode === "every") {
      const start = parseHhmm(from);
      const end = parseHhmm(to);
      if (start === null || end === null) return "times_invalid";
      if (end < start) return "between_invalid";
      return null;
    }
    const minutes: number[] = [];
    for (const time of times) {
      const value = parseHhmm(time);
      if (value === null) return "times_invalid";
      minutes.push(value);
    }
    if (minutes.length === 0) return "schedule_empty";
    minutes.sort((a, b) => a - b);
    for (let i = 1; i < minutes.length; i += 1) {
      if (minutes[i] - minutes[i - 1] < MIN_TIME_SPACING_MINUTES) return "times_too_close";
    }
    return null;
  }, [mode, times, from, to]);

  // The caller holds the draft in controlled mode, so every edit goes to it
  // the moment it is made. Through a ref: a caller that rebuilds its handler
  // each render must not make this fire on every render.
  const changeRef = useRef(onChange);
  useEffect(() => {
    changeRef.current = onChange;
  });
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    setTouched(true);
    changeRef.current?.(draft);
  }, [draft]);

  const preview = scheduleSentence(draft);
  const message =
    error ?? (touched && localProblem ? RHYTHM_ERROR_COPY[localProblem] : null);

  const toggleDay = (day: ScheduleDay) => {
    setSaved(false);
    setDays((current) =>
      current.includes(day)
        ? current.filter((entry) => entry !== day)
        : WEEK_ORDER.filter((entry) => entry === day || current.includes(entry)),
    );
  };

  const save = async () => {
    if (!onSave) return;
    setSaved(false);
    if (localProblem) {
      setError(RHYTHM_ERROR_COPY[localProblem] ?? null);
      return;
    }
    const code = await onSave(draft);
    setError(rhythmErrorLine(code));
    setSaved(code === null);
  };

  return (
    <div className="wrk-rhythm-editor" data-testid="wrk-rhythm-editor">
      {legacy && (
        <p className="wrk-srow-help" data-testid="wrk-rhythm-legacy">
          {RHYTHM_EDITOR_LEGACY_NOTE}
        </p>
      )}

      <div className="wrk-srow">
        <span className="wrk-srow-text">
          <span className="wrk-srow-title" id="wrk-rhythm-mode-title">
            {RHYTHM_EDITOR_TITLE}
          </span>
        </span>
        <span
          className="wrk-srow-controls"
          role="radiogroup"
          aria-labelledby="wrk-rhythm-mode-title"
        >
          {(
            [
              ["times", RHYTHM_EDITOR_MODE_TIMES],
              ["every", RHYTHM_EDITOR_MODE_EVERY],
            ] as const
          ).map(([value, label]) => (
            <label key={value} className="wrk-perm-label">
              <input
                type="radio"
                name="wrk-rhythm-mode"
                aria-label={label}
                checked={mode === value}
                disabled={disabled}
                onChange={() => {
                  setSaved(false);
                  setError(null);
                  setMode(value);
                }}
              />
              {label}
            </label>
          ))}
        </span>
      </div>

      {mode === "times" ? (
        <div className="wrk-srow">
          <span className="wrk-srow-text">
            <span className="wrk-srow-title">{RHYTHM_EDITOR_TIMES_LABEL}</span>
          </span>
          <span className="wrk-srow-controls">
            {times.map((time, index) => (
              <span key={index} className="wrk-rhythm-time">
                <input
                  type="time"
                  className="wrk-control wrk-control--time"
                  aria-label={`${RHYTHM_EDITOR_TIMES_LABEL} ${index + 1}`}
                  data-testid={`wrk-rhythm-time-${index}`}
                  value={time}
                  disabled={disabled}
                  onChange={(event) => {
                    setSaved(false);
                    setError(null);
                    setTimes((current) =>
                      current.map((entry, i) => (i === index ? event.target.value : entry)),
                    );
                  }}
                />
                {times.length > 1 && (
                  <button
                    type="button"
                    className="wrk-quiet-btn"
                    disabled={disabled}
                    data-testid={`wrk-rhythm-time-remove-${index}`}
                    onClick={() => {
                      setSaved(false);
                      setError(null);
                      setTimes((current) => current.filter((_, i) => i !== index));
                    }}
                  >
                    {RHYTHM_EDITOR_TIME_REMOVE}
                  </button>
                )}
              </span>
            ))}
            {times.length < MAX_TIMES && (
              <button
                type="button"
                className="wrk-quiet-btn"
                disabled={disabled}
                data-testid="wrk-rhythm-time-add"
                onClick={() => {
                  setSaved(false);
                  setError(null);
                  setTimes((current) => [...current, nextFreeTime(current)]);
                }}
              >
                {RHYTHM_EDITOR_TIME_ADD}
              </button>
            )}
          </span>
        </div>
      ) : (
        <div className="wrk-srow">
          <span className="wrk-srow-text">
            <span className="wrk-srow-title">{RHYTHM_EDITOR_EVERY_LABEL}</span>
          </span>
          <span className="wrk-srow-controls">
            <select
              className="wrk-control"
              aria-label={RHYTHM_EDITOR_EVERY_LABEL}
              data-testid="wrk-rhythm-every"
              value={String(everyMinutes)}
              disabled={disabled}
              onChange={(event) => {
                setSaved(false);
                setError(null);
                setEveryMinutes(Number(event.target.value));
              }}
            >
              {SCHEDULE_INTERVAL_MINUTES.map((minutes) => (
                <option key={minutes} value={minutes}>
                  {RHYTHM_INTERVAL_LABEL[minutes]}
                </option>
              ))}
            </select>
            <input
              type="time"
              className="wrk-control wrk-control--time"
              aria-label={RHYTHM_EDITOR_FROM_LABEL}
              data-testid="wrk-rhythm-from"
              value={from}
              disabled={disabled}
              onChange={(event) => {
                setSaved(false);
                setError(null);
                setFrom(event.target.value);
              }}
            />
            <input
              type="time"
              className="wrk-control wrk-control--time"
              aria-label={RHYTHM_EDITOR_TO_LABEL}
              data-testid="wrk-rhythm-to"
              value={to}
              disabled={disabled}
              onChange={(event) => {
                setSaved(false);
                setError(null);
                setTo(event.target.value);
              }}
            />
          </span>
        </div>
      )}

      <div className="wrk-srow">
        <span className="wrk-srow-text">
          <span className="wrk-srow-title" id="wrk-rhythm-days-title">
            {RHYTHM_EDITOR_DAYS_LABEL}
          </span>
        </span>
        <span className="wrk-srow-controls" aria-labelledby="wrk-rhythm-days-title">
          {WEEK_ORDER.map((day) => (
            <label key={day} className="wrk-perm-label">
              <input
                type="checkbox"
                aria-label={DAY_CHIP_LABEL[day]}
                checked={days.includes(day)}
                disabled={disabled}
                onChange={() => toggleDay(day)}
              />
              {DAY_CHIP_LABEL[day]}
            </label>
          ))}
          <button
            type="button"
            className="wrk-quiet-btn"
            disabled={disabled}
            data-testid="wrk-rhythm-preset-weekdays"
            onClick={() => {
              setSaved(false);
              setDays([...WEEKDAYS]);
            }}
          >
            {RHYTHM_EDITOR_PRESET_WEEKDAYS}
          </button>
          <button
            type="button"
            className="wrk-quiet-btn"
            disabled={disabled}
            data-testid="wrk-rhythm-preset-everyday"
            onClick={() => {
              setSaved(false);
              setDays([...WEEK_ORDER]);
            }}
          >
            {RHYTHM_EDITOR_PRESET_EVERY_DAY}
          </button>
        </span>
      </div>

      <div className="wrk-srow">
        <span className="wrk-srow-text">
          <span className="wrk-srow-title">{RHYTHM_EDITOR_TZ_LABEL}</span>
        </span>
        <span className="wrk-srow-controls">
          <select
            className="wrk-control"
            aria-label={RHYTHM_EDITOR_TZ_LABEL}
            data-testid="wrk-rhythm-tz"
            value={zone}
            disabled={disabled}
            onChange={(event) => {
              setSaved(false);
              setError(null);
              setZone(event.target.value);
            }}
          >
            {timezoneOptions(tz).map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </span>
      </div>

      <div className="wrk-srow">
        <span className="wrk-srow-text">
          {preview && (
            <span className="wrk-srow-title" data-testid="wrk-rhythm-preview">
              {preview}
            </span>
          )}
          {message && (
            <span className="wrk-rhythm-error" role="alert" data-testid="wrk-rhythm-error">
              {message}
            </span>
          )}
          {saved && !message && (
            <span className="wrk-srow-help" data-testid="wrk-rhythm-saved">
              {savedLabel ?? RHYTHM_EDITOR_SAVED}
            </span>
          )}
        </span>
        {onSave && (
          <span className="wrk-srow-controls">
            <button
              type="button"
              className="wrk-quiet-btn"
              disabled={disabled}
              data-testid="wrk-rhythm-save"
              onClick={() => void save()}
            >
              {saveLabel ?? RHYTHM_EDITOR_SAVE}
            </button>
          </span>
        )}
      </div>
    </div>
  );
}
